// LocalAgentResponseRenderer.js
// Renders the structured reasoning cards and diagnostic widgets inside the drawer.
// Pure rendering component designed for rich aesthetics.

import React from 'react';
import PropTypes from 'prop-types';
import { SEVERITY_COLORS, CONFIDENCE_LEVELS } from './localAgentConstants';
import './AgenticSupport.css';

const LocalAgentResponseRenderer = ({
    response,
    selectedKpis = [],
    onToggleKpiSelection,
    onFollowUpClick
}) => {
    if (!response || !response.success || !response.analysis) {
        return null;
    }

    const analysisData = response.analysis || {};

    const {
        summary,
        observations = [],
        rootCauses = [],
        drivers = [],
        solutions = [],
        recommendations = [],
        kpiSuggestions = [],
        anomalies = [],
        whatIf,
        followUpSuggestions = [],
        followUpQuestions = [],
        overallAlignment,
        kpiEvaluation = [],
        gaps = [],
        additionalKpiSuggestions = [],
        baseline,
        target,
        assumptions = [],
        notes = []
    } = analysisData;

    const evidence = response.evidence;
    const combinedKpiSuggestions = [...kpiSuggestions, ...additionalKpiSuggestions];

    return (
        <div className="la-response-container">
            {/* 1. Summary Section */}
            {summary && (
                <div className="la-summary-card">
                    <span className="la-section-title">Executive Summary</span>
                    <p className="la-summary-text">{summary}</p>
                </div>
            )}

            {/* 2. Observations List */}
            {observations.length > 0 && (
                <div>
                    <span className="la-section-title">Key Observations ({observations.length})</span>
                    {observations.map((obs, idx) => (
                        <div key={`obs-${idx}`} className="la-observation-item">
                            <div className="la-observation-header">
                                {obs.severity && (
                                    <div className="la-tag-group">
                                        <span 
                                            className="la-severity-chip" 
                                            style={{ backgroundColor: SEVERITY_COLORS[obs.severity] || SEVERITY_COLORS.high }}
                                        >
                                            {obs.severity}
                                        </span>
                                        {obs.function && <span className="la-context-chip">{obs.function}</span>}
                                        {obs.phase && <span className="la-context-chip">{obs.phase}</span>}
                                    </div>
                                )}
                                {obs.score !== undefined && obs.score !== null && (
                                    <span className="la-score-badge">Maturity: {obs.score}</span>
                                )}
                            </div>
                            {(obs.capability || obs.title) && <h5 className="la-observation-title">{obs.title || obs.capability}</h5>}
                            {(obs.insight || obs.description) && <p className="la-observation-insight">{obs.description || obs.insight}</p>}
                        </div>
                    ))}
                </div>
            )}

            {/* 3. Anomalies Section */}
            {anomalies.length > 0 && (
                <div>
                    <span className="la-section-title">Surprising Anomalies & Outliers ({anomalies.length})</span>
                    {anomalies.map((anom, idx) => (
                        <div key={`anom-${idx}`} className="la-anomaly-card">
                            <div className="la-anomaly-header">
                                <h5 className="la-anomaly-title">{anom.capability}</h5>
                                <span className="la-anomaly-dev-badge">{anom.deviation}</span>
                            </div>
                            <div className="la-anomaly-compare">
                                <span>Expected: {anom.expected}</span>
                                <span>|</span>
                                <span>Actual: {anom.actual}</span>
                            </div>
                            {anom.explanation && <p className="la-anomaly-text">{anom.explanation}</p>}
                        </div>
                    ))}
                </div>
            )}

            {/* 4. Root Causes & Drivers */}
            {(rootCauses.length > 0 || drivers.length > 0) && (
                <div>
                    <span className="la-section-title">Synthesized Drivers & Root Causes ({rootCauses.length + drivers.length})</span>
                    {rootCauses.map((rc, idx) => {
                        const confidence = CONFIDENCE_LEVELS[rc.confidence] || CONFIDENCE_LEVELS.medium;
                        return (
                            <div key={`rc-${idx}`} className="la-rootcause-card">
                                <div className="la-card-meta">
                                    <span className="la-card-title">{rc.area}</span>
                                    <span className="la-confidence-badge" style={{ color: confidence.color }}>
                                        <span className="la-confidence-dot" style={{ backgroundColor: confidence.color }} />
                                        {confidence.label} ({confidence.score})
                                    </span>
                                </div>
                                <p className="la-card-text">{rc.cause}</p>
                            </div>
                        );
                    })}
                    {drivers.map((drv, idx) => (
                        <div key={`drv-${idx}`} className="la-rootcause-card">
                            <div className="la-card-meta">
                                <span className="la-card-title">{drv.driver}</span>
                            </div>
                            {drv.evidence && <p className="la-card-text">{drv.evidence}</p>}
                            {drv.affectedCapabilities && drv.affectedCapabilities.length > 0 && (
                                <div className="la-solution-meta" style={{ marginTop: '8px' }}>
                                    <span className="la-meta-item">Affected: <strong>{drv.affectedCapabilities.join(', ')}</strong></span>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* 4.5. KPI Evaluation Section */}
            {kpiEvaluation.length > 0 && (
                <div>
                    <span className="la-section-title">
                        KPI Alignment Evaluation
                        {overallAlignment && <span style={{marginLeft: '10px', fontSize: '14px', fontWeight: 'normal', opacity: 0.8}}>(Overall: <strong style={{textTransform: 'capitalize'}}>{overallAlignment}</strong>)</span>}
                    </span>
                    {kpiEvaluation.map((kpi, idx) => {
                        const prioColor = kpi.alignment?.toLowerCase() === 'high' ? '#10b981' : 
                                         kpi.alignment?.toLowerCase() === 'medium' ? '#f59e0b' : '#ef4444';
                        return (
                            <div key={`kpi-eval-${idx}`} className="la-observation-item">
                                <div className="la-observation-header">
                                    <div className="la-tag-group">
                                        <span 
                                            className="la-severity-chip" 
                                            style={{ backgroundColor: prioColor }}
                                        >
                                            {kpi.alignment} Alignment
                                        </span>
                                    </div>
                                </div>
                                <h5 className="la-observation-title">{kpi.kpiName}</h5>
                                <p className="la-observation-insight">{kpi.reasoning}</p>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* 4.6. Gaps Section */}
            {gaps.length > 0 && (
                <div>
                    <span className="la-section-title">Identified Gaps ({gaps.length})</span>
                    {gaps.map((gap, idx) => (
                        <div key={`gap-${idx}`} className="la-rootcause-card" style={{ padding: '12px 16px' }}>
                            <p className="la-card-text" style={{ margin: 0 }}>• {gap}</p>
                        </div>
                    ))}
                </div>
            )}

            {/* 4.7. KPI Setup Baseline & Target */}
            {(baseline || target) && (
                <div>
                    <div style={{ backgroundColor: '#fffbe1', color: '#854d0e', padding: '10px 12px', borderRadius: '6px', fontSize: '13px', marginBottom: '16px', display: 'flex', alignItems: 'flex-start', border: '1px solid #fef08a' }}>
                        <span style={{ marginRight: '8px', fontSize: '16px' }}>💡</span>
                        <p style={{ margin: 0, lineHeight: 1.4 }}><strong>Note:</strong> Suggestions are FYI only — read and type the value yourself. No field is auto-filled.</p>
                    </div>
                    
                    <span className="la-section-title">KPI Setup Configuration</span>
                    
                    {baseline && (
                        <div className="la-solution-card">
                            <div className="la-card-meta">
                                <span className="la-card-title">Recommended Baseline</span>
                                <span className="la-priority-chip" style={{ backgroundColor: '#10b981' }}>
                                    {baseline.value}
                                </span>
                            </div>
                            {baseline.rationale && <p className="la-card-text">{baseline.rationale}</p>}
                            <div className="la-solution-meta">
                                {baseline.sourceAssumption && <span className="la-meta-item">Source: <strong>{baseline.sourceAssumption}</strong></span>}
                                {baseline.timeframe && baseline.timeframe !== 'n/a' && <span className="la-meta-item">Timeframe: <strong>{baseline.timeframe}</strong></span>}
                                {baseline.peerAnchor && baseline.peerAnchor !== 'no peer data' && <span className="la-meta-item">Anchor: <strong>{baseline.peerAnchor}</strong></span>}
                            </div>
                        </div>
                    )}

                    {target && (
                        <div className="la-solution-card">
                            <div className="la-card-meta">
                                <span className="la-card-title">Recommended Target</span>
                                <span className="la-priority-chip" style={{ backgroundColor: '#f59e0b' }}>
                                    {target.value}
                                </span>
                            </div>
                            {target.rationale && <p className="la-card-text">{target.rationale}</p>}
                            <div className="la-solution-meta">
                                {target.sourceAssumption && <span className="la-meta-item">Source: <strong>{target.sourceAssumption}</strong></span>}
                                {target.timeframe && target.timeframe !== 'n/a' && <span className="la-meta-item">Timeframe: <strong>{target.timeframe}</strong></span>}
                                {target.peerAnchor && target.peerAnchor !== 'no peer data' && <span className="la-meta-item">Anchor: <strong>{target.peerAnchor}</strong></span>}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* 4.8. KPI Setup Assumptions & Notes */}
            {(assumptions.length > 0 || notes.length > 0) && (
                <div>
                    {assumptions.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                            <span className="la-section-title" style={{ fontSize: '14px' }}>Underlying Assumptions</span>
                            <ul style={{ margin: 0, paddingLeft: '20px', color: '#4b5563', fontSize: '14px', lineHeight: '1.6' }}>
                                {assumptions.map((ass, idx) => <li key={`ass-${idx}`}>{ass}</li>)}
                            </ul>
                        </div>
                    )}
                    
                    {notes.length > 0 && (
                        <div>
                            <span className="la-section-title" style={{ fontSize: '14px' }}>Implementation Notes</span>
                            <ul style={{ margin: 0, paddingLeft: '20px', color: '#4b5563', fontSize: '14px', lineHeight: '1.6' }}>
                                {notes.map((note, idx) => <li key={`note-${idx}`}>{note}</li>)}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {/* 5. Solutions & Recommendations */}
            {(solutions.length > 0 || recommendations.length > 0) && (
                <div>
                    <span className="la-section-title">Recommended Transformation Solutions ({solutions.length + recommendations.length})</span>
                    {solutions.map((sol, idx) => {
                        const prioColor = sol.priority?.toLowerCase() === 'high' ? '#ef4444' : 
                                         sol.priority?.toLowerCase() === 'medium' ? '#f59e0b' : '#10b981';
                        return (
                            <div key={`sol-${idx}`} className="la-solution-card">
                                <div className="la-card-meta">
                                    <span className="la-card-title">Solution {idx + 1}</span>
                                    <span className="la-priority-chip" style={{ backgroundColor: prioColor }}>
                                        {sol.priority} Priority
                                    </span>
                                </div>
                                <p className="la-card-text">{sol.action}</p>
                                <div className="la-solution-meta">
                                    {sol.impact && <span className="la-meta-item">Impact: <strong>{sol.impact}</strong></span>}
                                    {sol.effort && <span className="la-meta-item">Effort: <strong>{sol.effort}</strong></span>}
                                </div>
                            </div>
                        );
                    })}
                    {recommendations.map((rec, idx) => {
                        const prioColor = rec.priority?.toLowerCase() === 'high' ? '#ef4444' : 
                                         rec.priority?.toLowerCase() === 'medium' ? '#f59e0b' : '#10b981';
                        return (
                            <div key={`rec-${idx}`} className="la-solution-card">
                                <div className="la-card-meta">
                                    <span className="la-card-title">Recommendation {idx + 1}</span>
                                    {rec.priority && (
                                        <span className="la-priority-chip" style={{ backgroundColor: prioColor }}>
                                            {rec.priority} Priority
                                        </span>
                                    )}
                                </div>
                                <p className="la-card-text">{rec.action}</p>
                                {rec.rationale && (
                                    <div className="la-solution-meta">
                                        <span className="la-meta-item">Rationale: <strong>{rec.rationale}</strong></span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* 6. What-If Scenario Analysis */}
            {whatIf && (
                <div>
                    <span className="la-section-title">Counterfactual Projection</span>
                    <div className="la-whatif-panel">
                        <span className="la-whatif-heading">💡 Scenario: {whatIf.scenario}</span>
                        <div className="la-whatif-grid">
                            <div className="la-whatif-col">
                                <span className="la-col-label">Current Heatmap</span>
                                {Object.entries(whatIf.currentDistribution || {}).map(([key, val]) => (
                                    <div key={`cur-${key}`} className="la-whatif-stat">
                                        <span style={{ textTransform: 'capitalize' }}>{key}</span>
                                        <strong>{val}</strong>
                                    </div>
                                ))}
                            </div>
                            <div className="la-whatif-arrow">→</div>
                            <div className="la-whatif-col">
                                <span className="la-col-label">Projected Matrix</span>
                                {Object.entries(whatIf.projectedDistribution || {}).map(([key, val]) => (
                                    <div key={`proj-${key}`} className="la-whatif-stat">
                                        <span style={{ textTransform: 'capitalize' }}>{key}</span>
                                        <strong style={{ color: key === 'severe' && val === '0%' ? '#22c55e' : '#818cf8' }}>{val}</strong>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 7. KPI Suggestions list (with select functionality if callbacks provided) */}
            {combinedKpiSuggestions.length > 0 && (
                <div>
                    <span className="la-section-title">Target KPIs & Alignment Metrics ({combinedKpiSuggestions.length})</span>
                    {combinedKpiSuggestions.map((kpi, idx) => {
                        const isSelected = selectedKpis.some(k => k.KPIName === (kpi.name || kpi.KPIName));
                        const kpiIdentifier = kpi.name || kpi.KPIName;
                        return (
                            <div 
                                key={`kpisug-${idx}`} 
                                className={`la-kpi-suggest-card ${isSelected ? 'selected' : ''}`}
                            >
                                {onToggleKpiSelection && (
                                    <input
                                        type="checkbox"
                                        className="la-kpi-checkbox"
                                        checked={isSelected}
                                        onChange={() => onToggleKpiSelection({
                                            KPIName: kpi.name || kpi.KPIName,
                                            Metric: kpi.metric || kpi.Metric,
                                            Purpose: kpi.purpose || kpi.Purpose,
                                            Stakeholders: kpi.stakeholders || kpi.Stakeholders || '',
                                            Industry: kpi.industry || kpi.Industry || ''
                                        })}
                                    />
                                )}
                                <div className="la-kpi-details">
                                    <span className="la-kpi-name">{kpiIdentifier}</span>
                                    {(kpi.metric || kpi.Metric) && (
                                        <span className="la-kpi-formula">Metric: {kpi.metric || kpi.Metric}</span>
                                    )}
                                    {(kpi.purpose || kpi.Purpose) && (
                                        <p className="la-kpi-purpose">{kpi.purpose || kpi.Purpose}</p>
                                    )}
                                    <div className="la-kpi-meta-row">
                                        {(kpi.baseline || kpi.Baseline) && <span>Baseline: <strong>{kpi.baseline || kpi.Baseline}</strong></span>}
                                        {(kpi.target || kpi.Target) && <span>Target: <strong>{kpi.target || kpi.Target}</strong></span>}
                                        {(kpi.frequency || kpi.Frequency) && <span>Freq: <strong>{kpi.frequency || kpi.Frequency}</strong></span>}
                                        {(kpi.stakeholders || kpi.Stakeholders) && <span>Owner: {kpi.stakeholders || kpi.Stakeholders}</span>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* 8. Guided Follow-Up Suggestions */}
            {(followUpSuggestions.length > 0 || followUpQuestions.length > 0) && onFollowUpClick && (
                <div className="la-followup-section">
                    <span className="la-section-title">Suggested Deep Dives</span>
                    <div className="la-followup-chips">
                        {(followUpSuggestions.length > 0 ? followUpSuggestions : followUpQuestions).map((query, idx) => (
                            <button
                                key={`fup-${idx}`}
                                type="button"
                                className="la-followup-chip"
                                onClick={() => onFollowUpClick(query)}
                            >
                                ✦ {query}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* 9. Evidence Context */}
            {evidence && evidence.status === 'ok' && (
                <div style={{ marginTop: '20px' }}>
                    <span className="la-section-title">Grounded Evidence Context</span>
                    <div className="la-whatif-panel" style={{ backgroundColor: 'rgba(59, 130, 246, 0.05)', borderColor: 'rgba(59, 130, 246, 0.2)' }}>
                        {evidence.company && (
                            <div className="la-solution-meta" style={{ marginBottom: '8px' }}>
                                <span className="la-meta-item" style={{ color: '#1e293b' }}>Company: <strong>{evidence.company.name} ({evidence.company.industry})</strong></span>
                            </div>
                        )}
                        {evidence.benchmark && (
                            <div className="la-solution-meta">
                                <span className="la-meta-item">Metric: <strong>{evidence.benchmark.metricName}</strong></span>
                                <span className="la-meta-item">Value: <strong>{evidence.benchmark.companyValue} {evidence.benchmark.unit}</strong></span>
                                <span className="la-meta-item">Industry Mean: <strong>{evidence.benchmark.industryMean} {evidence.benchmark.unit}</strong></span>
                                {evidence.benchmark.percentile && <span className="la-meta-item">Percentile: <strong>{evidence.benchmark.percentile}%</strong></span>}
                            </div>
                        )}
                        {evidence.sources && evidence.sources.length > 0 && (
                            <div className="la-solution-meta" style={{ marginTop: '8px' }}>
                                <span className="la-meta-item">Sources: <strong>{evidence.sources.map(s => s.source).join(', ')}</strong></span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

LocalAgentResponseRenderer.propTypes = {
    response: PropTypes.object,
    selectedKpis: PropTypes.array,
    onToggleKpiSelection: PropTypes.func,
    onFollowUpClick: PropTypes.func
};

export default LocalAgentResponseRenderer;
